import { useState } from "react";
import { Camera, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { uploadPhoto, mediaUrl } from "@/lib/photos";
import { useLanguage } from "@/i18n";

export const PhotoUploader = ({ photos = [], onChange, max = 6, testId = "photo-uploader", hint, onOfflineFile }) => {
  const { t } = useLanguage();
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const token = localStorage.getItem("devisto_token");

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList).slice(0, Math.max(0, max - photos.length));
    if (!files.length) return;
    setBusy(true);
    const next = [...photos];
    try {
      for (let i = 0; i < files.length; i += 1) {
        const res = await uploadPhoto(files[i], {
          token,
          onProgress: (p) => setProgress(Math.round(((i + p / 100) / files.length) * 100)),
        });
        next.push(res.id);
        onChange([...next]);
      }
      toast.success(files.length > 1 ? t("Photos ajoutées.") : t("Photo ajoutée."));
    } catch (e) {
      if (!e.response && onOfflineFile) {
        files.forEach((f) => onOfflineFile(f));
        toast.success(t("Photos gardées sur l'appareil — envoi au retour du réseau."));
      } else {
        const detail = e.response?.data?.detail;
        toast.error(typeof detail === "string" ? detail : t("Envoi de la photo impossible. Réessayez."));
      }
    } finally {
      setBusy(false);
      setProgress(0);
    }
  };

  return (
    <div data-testid={testId}>
      <div className="flex flex-wrap gap-3">
        {photos.map((id) => (
          <div key={id} data-testid={`${testId}-thumb-${id}`} className="relative w-20 h-20 rounded-xl overflow-hidden border border-[#E2E7F7] bg-slate-100">
            <img src={mediaUrl(id)} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
            <button
              type="button"
              data-testid={`${testId}-remove-${id}`}
              onClick={() => onChange(photos.filter((p) => p !== id))}
              aria-label={t("Supprimer la photo")}
              className="absolute top-1 right-1 inline-flex items-center justify-center w-5 h-5 rounded-full bg-white/90 text-[#5C6A87] hover:text-red-600 shadow-sm transition-colors"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        ))}
        {photos.length < max && (
          <label className="w-20 h-20 inline-flex flex-col items-center justify-center gap-1 rounded-xl border border-dashed border-[#E2E7F7] bg-white text-[#5C6A87] cursor-pointer hover:border-[#3A5CC8] hover:text-[#3A5CC8] transition-colors duration-200">
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Camera className="w-5 h-5" />}
            <span className="text-[10px] font-semibold uppercase tracking-wide">{t("Photo")}</span>
            <input
              data-testid={`${testId}-input`}
              type="file"
              accept="image/*,.heic,.heif"
              multiple
              className="hidden"
              disabled={busy}
              onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }}
            />
          </label>
        )}
      </div>
      {busy && (
        <div data-testid={`${testId}-progress`} className="mt-3 h-1.5 w-full max-w-xs rounded-full bg-slate-200 overflow-hidden">
          <div className="h-full bg-[#3A5CC8] transition-[width] duration-200" style={{ width: `${progress}%` }} />
        </div>
      )}
      <p className="mt-2 text-xs text-[#5C6A87]">
        {hint || t("JPG, PNG ou photo iPhone (HEIC) — compressées automatiquement avant l'envoi.")}
      </p>
    </div>
  );
};
